// src/lib/chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend,
  Title,
  SubTitle,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register all plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend,
  Title,
  SubTitle,
  zoomPlugin,
  annotationPlugin,
);

// Default brand colors
export const brandColors = {
  primary: '#8b5cf6', // Purple
  secondary: '#ec4899', // Pink
  accent: '#06b6d4', // Cyan
  success: '#10b981', // Emerald
  warning: '#f59e0b', // Amber
  error: '#ef4444', // Red
  info: '#3b82f6', // Blue
  dark: '#1e293b',
  light: '#f8fafc',
};

// Get theme-aware colors
export const getThemeColors = isDark => ({
  text: isDark ? '#f1f5f9' : '#1e293b',
  subtitle: isDark ? '#94a3b8' : '#64748b',
  grid: isDark ? '#334155' : '#e2e8f0',
  background: isDark ? '#0f172a' : '#ffffff',
  tooltipBg: isDark ? '#1e293b' : '#ffffff',
  tooltipBorder: isDark ? '#334155' : '#e2e8f0',
});

// Default chart options with animations and responsiveness
export const getDefaultChartOptions = (isDark, customOptions = {}) => {
  const themeColors = getThemeColors(isDark);

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 800,
      easing: 'easeInOutQuart',
      animateScale: true,
      animateRotate: true,
    },
    transitions: {
      show: {
        animations: {
          x: { from: 0 },
          y: { from: 0 },
          opacity: { from: 0 },
        },
      },
      hide: {
        animations: {
          x: { to: 0 },
          y: { to: 0 },
          opacity: { to: 0 },
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: {
          color: themeColors.text,
          font: {
            size: 11,
            family: "'Inter', system-ui, sans-serif",
            weight: 'normal',
          },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          padding: 12,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: themeColors.tooltipBg,
        titleColor: themeColors.text,
        bodyColor: themeColors.subtitle,
        borderColor: themeColors.tooltipBorder,
        borderWidth: 1,
        cornerRadius: 8,
        titleFont: {
          size: 12,
          weight: 'bold',
          family: "'Inter', system-ui, sans-serif",
        },
        bodyFont: {
          size: 11,
          family: "'Inter', system-ui, sans-serif",
        },
        padding: 10,
        caretSize: 6,
        displayColors: true,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== undefined) {
              label += new Intl.NumberFormat().format(context.parsed.y);
            }
            return label;
          },
        },
      },
      zoom: {
        zoom: {
          wheel: { enabled: false },
          pinch: { enabled: false },
          mode: 'xy',
        },
        pan: {
          enabled: false,
        },
      },
      annotation: {
        annotations: {},
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'xy',
      intersect: false,
    },
    ...customOptions,
  };
};

// Line chart specific options
export const getLineChartOptions = (isDark, customOptions = {}) => {
  const themeColors = getThemeColors(isDark);
  const baseOptions = getDefaultChartOptions(isDark, customOptions);

  return {
    ...baseOptions,
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 2,
        borderJoin: 'round',
        capBezierPoints: true,
        fill: true,
      },
      point: {
        radius: 3,
        hoverRadius: 6,
        pointStyle: 'circle',
        borderWidth: 2,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: themeColors.grid,
          drawBorder: false,
          lineWidth: 0.5,
        },
        ticks: {
          color: themeColors.subtitle,
          font: { size: 10, family: "'Inter', system-ui, sans-serif" },
          stepSize: undefined,
          callback: function (value) {
            if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
            return value;
          },
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: themeColors.subtitle,
          font: { size: 10, family: "'Inter', system-ui, sans-serif" },
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8,
        },
      },
    },
  };
};

// Bar chart specific options
export const getBarChartOptions = (isDark, customOptions = {}) => {
  const themeColors = getThemeColors(isDark);
  const baseOptions = getDefaultChartOptions(isDark, customOptions);

  return {
    ...baseOptions,
    elements: {
      bar: {
        borderRadius: 8,
        borderSkipped: false,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: themeColors.grid,
          drawBorder: false,
          lineWidth: 0.5,
        },
        ticks: {
          color: themeColors.subtitle,
          font: { size: 10, family: "'Inter', system-ui, sans-serif" },
          callback: function (value) {
            if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
            return value;
          },
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: themeColors.subtitle,
          font: { size: 10, family: "'Inter', system-ui, sans-serif" },
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
        },
      },
    },
  };
};

// Pie/Doughnut chart specific options
export const getPieChartOptions = (isDark, customOptions = {}) => {
  const themeColors = getThemeColors(isDark);
  const baseOptions = getDefaultChartOptions(isDark, customOptions);

  return {
    ...baseOptions,
    cutout: customOptions.cutout || '50%',
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        ...baseOptions.plugins.tooltip,
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
      legend: {
        ...baseOptions.plugins.legend,
        position: 'right',
        align: 'center',
        labels: {
          ...baseOptions.plugins.legend.labels,
          generateLabels: function (chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i];
                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return {
                  text: `${label} (${percentage}%)`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
    },
  };
};

// Responsive chart wrapper component
export const ResponsiveChartWrapper = ({
  children,
  height = '100%',
  minHeight = 200,
}) => {
  return (
    <div style={{ height, minHeight, width: '100%', position: 'relative' }}>
      {children}
    </div>
  );
};

// Get chart container style
export const getChartContainerStyle = (height = '100%', minHeight = 200) => ({
  height,
  minHeight,
  width: '100%',
  position: 'relative',
});

// Gradient generator for charts
export const createGradient = (ctx, color1, color2, height) => {
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, color1);
  gradient.addColorStop(1, color2);
  return gradient;
};

// Get dataset with theme colors
export const getThemedDataset = (data, color, isDark, options = {}) => {
  return {
    data,
    backgroundColor: typeof color === 'string' ? color : color[0],
    borderColor: typeof color === 'string' ? color : color[1],
    borderWidth: options.borderWidth || 2,
    borderRadius: options.borderRadius || 8,
    tension: options.tension || 0.4,
    fill: options.fill !== undefined ? options.fill : true,
    pointRadius: options.pointRadius || 3,
    pointHoverRadius: options.pointHoverRadius || 6,
    pointBackgroundColor: typeof color === 'string' ? color : color[1],
    pointBorderColor: options.pointBorderColor || (isDark ? '#fff' : '#000'),
    label: options.label,
    ...options,
  };
};

export default ChartJS;
